import { X } from "lucide-react";
import { Dialog } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";

/**
 * Не закрывать окно, если взаимодействие было внутри другого диалога/тоста (например, confirm поверх Drawer):
 * для Radix это «снаружи», т.к. окна в разных порталах.
 */
const guardOutside = (dismissible: boolean) => (e: Event) => {
  const target = (e as CustomEvent<{ originalEvent?: Event }>).detail?.originalEvent?.target ?? e.target;
  if (!dismissible || (target instanceof Element && target.closest('[role="dialog"], [role="alertdialog"], [data-sonner-toaster]'))) e.preventDefault();
};

const sizes = { sm: "sm:max-w-sm", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl", full: "sm:max-w-[min(96vw,1400px)] sm:h-[92dvh]" };

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Кнопки действий. На mobile — столбиком на всю ширину, первая (главная) сверху. */
  footer?: ReactNode;
  size?: keyof typeof sizes;
  /** false — нельзя закрыть кликом вне/Esc/крестиком (идёт операция, обязательное действие). */
  dismissible?: boolean;
  hideClose?: boolean;
  className?: string;
  bodyClassName?: string;
  /** Без padding у тела (списки, таблицы во всю ширину). */
  flush?: boolean;
  /** Доступное имя окна, когда видимого заголовка нет (alert/confirm рисуют свой). */
  ariaTitle?: string;
};

/** Desktop/tablet — диалог по центру; < 640px — bottom sheet с безопасной зоной снизу. */
export function Modal({
  open, onOpenChange, title, description, children, footer, size = "md", dismissible = true, hideClose, className, bodyClassName, flush, ariaTitle,
}: ModalProps) {
  const text = useUiText();
  const block = (e: Event) => { if (!dismissible) e.preventDefault(); };
  const outside = guardOutside(dismissible);
  return (
    <Dialog.Root open={open} onOpenChange={(o) => (dismissible || o) && onOpenChange(o)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-overlay/50 backdrop-blur-[2px] data-[state=open]:animate-[ui-in_150ms_ease-out]" />
        <Dialog.Content
          onPointerDownOutside={outside}
          onInteractOutside={outside}
          onEscapeKeyDown={block}
          {...(!description && { "aria-describedby": undefined })}
          className={cn(
            "fixed z-50 flex flex-col bg-surface text-fg shadow-2xl outline-none",
            "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl pb-[env(safe-area-inset-bottom)] data-[state=open]:animate-sheet-in",
            "sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[88dvh] sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-card sm:pb-0 sm:data-[state=open]:animate-in",
            sizes[size],
            className,
          )}
        >
          <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-border sm:hidden" aria-hidden />
          {!title && hideClose && <Dialog.Title className="sr-only">{ariaTitle ?? text.dialog}</Dialog.Title>}
          {(title || !hideClose) && (
            <div className={cn("flex shrink-0 items-start gap-2 px-6 pt-4 sm:pt-6", title ? "border-b border-border pb-4" : "pb-0")}>
              <div className="min-w-0 flex-1">
                {title ? <Dialog.Title className="text-xl leading-normal font-semibold">{title}</Dialog.Title> : <Dialog.Title className="sr-only">{ariaTitle ?? text.dialog}</Dialog.Title>}
                {description && <Dialog.Description className="mt-1 text-sm leading-[1.4] text-muted">{description}</Dialog.Description>}
              </div>
              {!hideClose && dismissible && (
                <Dialog.Close aria-label={text.close} className="-me-2 -mt-1 grid size-10 shrink-0 place-items-center rounded-full text-fg hover:bg-surface-2">
                  <X className="size-6" strokeWidth={1.5} />
                </Dialog.Close>
              )}
            </div>
          )}
          {children !== undefined && (
            <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", !flush && "px-6 py-6", bodyClassName)}>{children}</div>
          )}
          {footer && (
            <div className="flex shrink-0 flex-col gap-3 px-6 pt-2 pb-6 sm:flex-row-reverse [&>*]:max-sm:w-full sm:[&>*]:flex-1">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export type DrawerProps = Omit<ModalProps, "size"> & { width?: "sm" | "md" | "lg" };
const widths = { sm: "sm:w-[380px]", md: "sm:w-[480px]", lg: "sm:w-[720px]" };

/** Боковая панель справа (фильтры, карточка записи); на mobile — bottom sheet. */
export function Drawer({ open, onOpenChange, title, description, children, footer, width = "md", dismissible = true, className, bodyClassName, flush }: DrawerProps) {
  const text = useUiText();
  const block = (e: Event) => { if (!dismissible) e.preventDefault(); };
  const outside = guardOutside(dismissible);
  return (
    <Dialog.Root open={open} onOpenChange={(o) => (dismissible || o) && onOpenChange(o)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-overlay/40" />
        <Dialog.Content
          onPointerDownOutside={outside}
          onInteractOutside={outside}
          onEscapeKeyDown={block}
          {...(!description && { "aria-describedby": undefined })}
          className={cn(
            "fixed z-50 flex flex-col bg-surface text-fg shadow-2xl outline-none",
            "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl pb-[env(safe-area-inset-bottom)] data-[state=open]:animate-sheet-in",
            "sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:max-w-[100vw] sm:rounded-none sm:pb-0 sm:data-[state=open]:animate-[ui-drawer-in_200ms_ease-out]",
            widths[width],
            className,
          )}
        >
          <div className="flex shrink-0 items-start gap-2 border-b border-border px-6 pt-6 pb-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-xl leading-normal font-semibold">{title ?? ""}</Dialog.Title>
              {description && <Dialog.Description className="mt-1 text-sm text-muted">{description}</Dialog.Description>}
            </div>
            {dismissible && (
              <Dialog.Close aria-label={text.close} className="-me-2 grid size-10 place-items-center rounded-full text-fg hover:bg-surface-2">
                <X className="size-6" strokeWidth={1.5} />
              </Dialog.Close>
            )}
          </div>
          <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", !flush && "p-6", bodyClassName)}>{children}</div>
          {footer && <div className="flex shrink-0 flex-col gap-3 border-t border-border px-6 py-4 sm:flex-row-reverse [&>*]:max-sm:w-full sm:[&>*]:flex-1">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
